import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { createPortal } from 'react-dom';
import {
    MDBDropdown,
    MDBDropdownItem,
    MDBDropdownLink,
    MDBDropdownMenu,
    MDBDropdownToggle
} from 'mdb-react-ui-kit';
import './DropdownSelect.css';
import { Insight, VisualisationAttribute } from "konect-model";
import { nanoid } from "nanoid";

interface DropdownSelectProps {
    type: string;
    value: string;
    defaultValue: string;
    items: DropdownItem[];
    onClick: (e: any) => void;
}

export class DropdownItemDummy {
    name: string;
    id: string; 
    description?: string;
    imageUrl?: string;

    constructor(name: string, id: string, description?: string, imageUrl?: string) {
        this.name = name;
        this.id = id;
        if (description) this.description = description;
        if (imageUrl) this.imageUrl = imageUrl;
    }
}

type DropdownItem = VisualisationAttribute | Insight | DropdownItemDummy;

export default function DropdownSelect(props: DropdownSelectProps) {
    const ref = useRef<any>();
    const dropdownRef = useRef<any>();
    const [itemId, setItemId] = useState<number>(-1);
    const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>();
    const value = props.value || props.defaultValue;
    const items = props.items;
    const entry = items.find(i => i.name === value || value.includes(i.name));

    const [showPopUp, setShowPopup] = useState(false);

    const getTooltipStyle = (el?: any): CSSProperties => {
        let offset = ref.current ? -ref.current.scrollTop : 0;
        if (el) {
            let height = document.documentElement.clientHeight;
            let additionalOffset = ref.current.getBoundingClientRect().top + el.offsetTop + el.offsetHeight - height;
            if (additionalOffset > 0) {
                offset -= additionalOffset;
            }
        }
        return { marginTop: "calc(-0.5rem + " + offset + "px)" }
    }

    useEffect(() => {
        setTooltipStyle(getTooltipStyle(document.getElementById("tooltip" + itemId)));
    }, [itemId])

    return (<div ref={dropdownRef}>
    <MDBDropdown>
        <MDBDropdownToggle type="button" className="dropdown-toggle" onMouseEnter={() => {setShowPopup(true)}} onMouseLeave={() => setShowPopup(false)}>{value}
            {showPopUp && createPortal(entry && (entry.imageUrl || entry.description) && <div className="dropdown-popup" style={{top: dropdownRef.current.getBoundingClientRect().top + "px", 
                left: (dropdownRef.current.getBoundingClientRect().left + dropdownRef.current.getBoundingClientRect().width/2)}}>
                    {entry.imageUrl ? <img src={entry.imageUrl} className="img-fluid mb-2" alt={""}/> : entry.description}
                </div>,
            document.body)}
        </MDBDropdownToggle>
        <MDBDropdownMenu>
            <div className="dropdown-wrapper rounded-5" /** Without this wrapper the tooltips wont work. */
                ref={ref}
                onScroll={() => setTooltipStyle({ opacity: 0 })}>
                {items.filter(item => !!item).map((item: DropdownItem, index) => 
                    <MDBDropdownItem key={"di_" + (item.name || item)} className={value === item.name ? "active" : ""}>
                        <MDBDropdownLink
                            className="dropdown-select-link"
                            name={props.type}
                            data-mssg={JSON.stringify(item)}
                            onClick={props.onClick}
                            value={item.name || item}
                            renderer={(item.name || item)}
                            tag="button" type="button"
                            key={item.id}
                            onMouseEnter={() => { setItemId(index) }}
                            onMouseLeave={() => { setTooltipStyle(getTooltipStyle()); setItemId(-1) }}>

                            <div className="dropdown-tooltip p-3 rounded-5 shadow-5"
                                id={"tooltip" + index}
                                style={tooltipStyle}>
                                {item.imageUrl && <img src={item.imageUrl} className="img-fluid mb-2" alt={""}/>}
                                {item.description || "No description available"}
                            </div>
                            
                            {item.name || item} {props.type === "selectedAttribute" ? `(${index})` : ""}
                        </MDBDropdownLink>
                    </MDBDropdownItem>
                )}
            </div>
        </MDBDropdownMenu>
    </MDBDropdown>
    </div>)
}

export function createDropdownItemArray(names: string[], ids?: string[], descriptions?: string[], imageUrls?: string[]) {
    const items: DropdownItemDummy[] = [];
    names.forEach((name, index) => {
        const id = ids ? ids[index] : nanoid();
        const description = descriptions ? descriptions[index] : undefined
        const imageUrl = imageUrls ? imageUrls[index] : undefined;
        items.push(new DropdownItemDummy(name, id, description, imageUrl));
    });
    return items;
}
